import { PERF_EVENT, type PerfMetricDetail } from '@/lib/perfMetrics';

export const PERF_SUMMARY_EVENT = 'app:perfMetricSummary';

export type PerfMetricSummaryItem = {
  name: string;
  count: number;
  sampleCount: number;
  p50Ms: number | null;
  p95Ms: number | null;
  avgMs: number | null;
  minMs: number | null;
  maxMs: number | null;
};

export type PerfMetricSummaryDetail = {
  ts: number;
  windowMs: number;
  appEnv: string;
  mode: string;
  metrics: PerfMetricSummaryItem[];
};

const TRUE_VALUES = new Set(['1', 'true', 'yes', 'on']);
const FALSE_VALUES = new Set(['0', 'false', 'no', 'off']);
const DEFAULT_FLUSH_INTERVAL_MS = 60_000;
const DEFAULT_MAX_SAMPLES_PER_METRIC = 300;
const DEFAULT_MAX_QUEUE_SIZE = 500;

let initialized = false;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let intervalWindowStartedAt = Date.now();
let metricEventListener: ((event: Event) => void) | null = null;
let pagehideEventListener: (() => void) | null = null;

const metricQueue: PerfMetricDetail[] = [];
const metricCounts = new Map<string, number>();
const durationSamples = new Map<string, number[]>();

function parseBooleanFlag(raw: string | undefined): boolean | null {
  if (!raw) return null;
  const normalized = raw.trim().toLowerCase();
  if (TRUE_VALUES.has(normalized)) return true;
  if (FALSE_VALUES.has(normalized)) return false;
  return null;
}

function parsePositiveInt(raw: string | undefined, fallback: number): number {
  const parsed = Number(raw ?? '');
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.floor(parsed);
}

function getAppEnv(): string {
  const appEnv = String(import.meta.env.VITE_APP_ENV ?? '').trim();
  if (appEnv) return appEnv.toLowerCase();
  return String(import.meta.env.MODE ?? '').trim().toLowerCase();
}

function isStagingEnv(): boolean {
  return getAppEnv() === 'staging';
}

function isCollectorEnabled(): boolean {
  const explicit = parseBooleanFlag(import.meta.env.VITE_PERF_METRIC_ENABLED as string | undefined);
  if (explicit !== null) return explicit;
  return isStagingEnv();
}

function quantile(sortedValues: number[], q: number): number {
  if (sortedValues.length === 0) return 0;
  if (sortedValues.length === 1) return sortedValues[0];
  const position = (sortedValues.length - 1) * q;
  const lowerIndex = Math.floor(position);
  const upperIndex = Math.ceil(position);
  if (lowerIndex === upperIndex) return sortedValues[lowerIndex];
  const weight = position - lowerIndex;
  return sortedValues[lowerIndex] + (sortedValues[upperIndex] - sortedValues[lowerIndex]) * weight;
}

function appendSample(metricName: string, durationMs: number, maxSamplesPerMetric: number): void {
  let samples = durationSamples.get(metricName);
  if (!samples) {
    samples = [];
    durationSamples.set(metricName, samples);
  }
  if (samples.length >= maxSamplesPerMetric) {
    samples.shift();
  }
  samples.push(durationMs);
}

function buildSummary(windowMs: number): PerfMetricSummaryDetail {
  const metrics: PerfMetricSummaryItem[] = [];

  for (const [name, count] of metricCounts.entries()) {
    const samples = durationSamples.get(name) ?? [];
    const sorted = [...samples].sort((a, b) => a - b);
    const sampleCount = sorted.length;

    const p50Ms = sampleCount > 0 ? quantile(sorted, 0.5) : null;
    const p95Ms = sampleCount > 0 ? quantile(sorted, 0.95) : null;
    const minMs = sampleCount > 0 ? sorted[0] : null;
    const maxMs = sampleCount > 0 ? sorted[sampleCount - 1] : null;
    const avgMs = sampleCount > 0 ? sorted.reduce((sum, value) => sum + value, 0) / sampleCount : null;

    metrics.push({ name, count, sampleCount, p50Ms, p95Ms, avgMs, minMs, maxMs });
  }

  metrics.sort((a, b) => a.name.localeCompare(b.name));

  return {
    ts: Date.now(),
    windowMs,
    appEnv: getAppEnv(),
    mode: String(import.meta.env.MODE ?? ''),
    metrics,
  };
}

function emitSummary(detail: PerfMetricSummaryDetail): void {
  window.dispatchEvent(new CustomEvent<PerfMetricSummaryDetail>(PERF_SUMMARY_EVENT, { detail }));
}

function clearCollectedMetrics(): void {
  metricCounts.clear();
  durationSamples.clear();
}

async function postPayload(endpoint: string, payload: unknown, useBeacon: boolean): Promise<void> {
  try {
    const body = JSON.stringify(payload);
    if (useBeacon && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(endpoint, blob)) return;
    }
    await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'include',
      keepalive: true,
    });
  } catch {
    // Metrics transport failures must never affect UX flow.
  }
}

function installDebugBridge(flushNow: () => void): void {
  const typedWindow = window as Window & {
    __askyouraiPerfMetrics?: {
      flushNow: () => void;
      snapshot: () => PerfMetricSummaryDetail;
      reset: () => void;
    };
  };

  typedWindow.__askyouraiPerfMetrics = {
    flushNow,
    snapshot: () => buildSummary(Date.now() - intervalWindowStartedAt),
    reset: () => {
      metricQueue.length = 0;
      clearCollectedMetrics();
      intervalWindowStartedAt = Date.now();
    },
  };
}

export function initPerfMetricCollector(): void {
  if (initialized || typeof window === 'undefined') return;
  if (!isCollectorEnabled()) return;

  const endpoint = String(import.meta.env.VITE_PERF_METRIC_ENDPOINT ?? '').trim();
  const flushIntervalMs = parsePositiveInt(
    import.meta.env.VITE_PERF_METRIC_FLUSH_MS as string | undefined,
    DEFAULT_FLUSH_INTERVAL_MS,
  );
  const maxSamplesPerMetric = parsePositiveInt(
    import.meta.env.VITE_PERF_METRIC_MAX_SAMPLES as string | undefined,
    DEFAULT_MAX_SAMPLES_PER_METRIC,
  );
  const maxQueueSize = parsePositiveInt(
    import.meta.env.VITE_PERF_METRIC_MAX_QUEUE as string | undefined,
    DEFAULT_MAX_QUEUE_SIZE,
  );

  const flush = (reason: 'interval' | 'manual' | 'pagehide'): Promise<void> => {
    const now = Date.now();
    const summary = buildSummary(now - intervalWindowStartedAt);
    emitSummary(summary);

    const queued = metricQueue.splice(0, metricQueue.length);
    clearCollectedMetrics();
    intervalWindowStartedAt = now;

    if (!endpoint) return Promise.resolve();

    return postPayload(endpoint, {
      reason,
      ts: now,
      appEnv: summary.appEnv,
      mode: summary.mode,
      summary,
      events: queued,
    }, reason === 'pagehide');
  };

  const onMetricEvent = (event: Event): void => {
    const customEvent = event as CustomEvent<PerfMetricDetail>;
    const detail = customEvent.detail;
    if (!detail || typeof detail.name !== 'string' || !detail.name.trim()) return;

    const metricName = detail.name.trim();
    const prevCount = metricCounts.get(metricName) ?? 0;
    metricCounts.set(metricName, prevCount + 1);

    const durationMs = Number(detail.durationMs);
    if (Number.isFinite(durationMs) && durationMs >= 0) {
      appendSample(metricName, durationMs, maxSamplesPerMetric);
    }

    if (metricQueue.length >= maxQueueSize) {
      metricQueue.shift();
    }
    metricQueue.push({
      ...detail,
      name: metricName,
      ts: Number.isFinite(detail.ts) ? detail.ts : Date.now(),
    });
  };

  metricEventListener = onMetricEvent;
  pagehideEventListener = () => {
    void flush('pagehide');
  };

  window.addEventListener(PERF_EVENT, metricEventListener as EventListener);
  window.addEventListener('pagehide', pagehideEventListener);

  flushTimer = setInterval(() => {
    void flush('interval');
  }, flushIntervalMs);

  installDebugBridge(() => {
    void flush('manual');
  });

  initialized = true;
}

export function stopPerfMetricCollector(): void {
  if (!initialized) return;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
  if (metricEventListener) {
    window.removeEventListener(PERF_EVENT, metricEventListener as EventListener);
    metricEventListener = null;
  }
  if (pagehideEventListener) {
    window.removeEventListener('pagehide', pagehideEventListener);
    pagehideEventListener = null;
  }
  initialized = false;
}
